import type { BlockingProposalInfo } from '@/types/vault';

/**
 * Error carrying the backend business errorCode (and extra payload fields)
 * so pages can map codes to human copy instead of parsing message strings.
 * All enterprise endpoints reply HTTP 200 with { success: false, error,
 * errorCode } inside data — this surfaces that envelope to callers.
 */
export class EnterpriseApiError extends Error {
  errorCode?: string;
  /** GROUP_IN_USE deletes: names of the policy rules referencing the group. */
  referencingRules?: string[];
  /**
   * FUNDS_RESERVED / VAULT_BUSY / VAULT_DEPLOYMENT_PENDING (create, amend):
   * the in-flight proposals that block this one.
   */
  blockingProposals?: BlockingProposalInfo[];

  constructor(
    message: string,
    errorCode?: string,
    referencingRules?: string[],
    blockingProposals?: BlockingProposalInfo[]
  ) {
    super(message);
    this.name = 'EnterpriseApiError';
    this.errorCode = errorCode;
    this.referencingRules = referencingRules;
    this.blockingProposals = blockingProposals;
  }
}

/**
 * Build the error for a `{ success: false, error | message, errorCode }`
 * envelope, keeping the errorCode so friendlyError() can show human copy
 * (e.g. INVALID_CHALLENGE_TOKEN, PUBKEY_MISMATCH, GOVERNANCE_REQUIRED).
 */
export function apiFailure(
  data: unknown,
  fallback: string,
  messageField: 'error' | 'message' = 'error'
): EnterpriseApiError {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const text = d[messageField];
  const code = d['errorCode'];
  return new EnterpriseApiError(
    typeof text === 'string' && text.length > 0 ? text : fallback,
    typeof code === 'string' ? code : undefined
  );
}
